import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, mensagemDoErro } from './api';
import { useSessao } from './sessao';
import { Aviso, Carregando, Dialogo } from './ui';

/**
 * A lixeira nas listas — onda 246 (`RN-214`, a exclusão definitiva da 235).
 *
 * «Coloque um símbolo de lixeira ao lado de cada item nas listas, ao clicar
 *  aparece um pop-up de confirmação. Assim consigo limpar o sistema
 *  corretamente mais rápido e de forma eficiente.»
 *                                            — Humberto, 02/10/2026
 *
 * A tela `/exclusao-definitiva` tem três passos (procurar, ver o que sai,
 * digitar o código e segurar). Numa lista, o primeiro passo já foi dado —
 * o registro está na linha — e o terceiro vira o clique de confirmação na
 * janela. O do meio continua: a janela abre rodando a SIMULAÇÃO, e o botão
 * de apagar só aparece depois que ela diz o que vai junto. Quem limpa uma
 * OS precisa ver as 40 amostras e os 300 ensaios antes do clique, não
 * depois.
 *
 * As fechaduras são as mesmas da 235 — a lixeira só chama as rotas
 * `simular` e `executar`. Para quem não tem a marca (ou numa instalação
 * sem `EXCLUSAO_DEFINITIVA=sim`), não desenha nada: nem o botão, nem a
 * coluna.
 */

interface Alvo { id: string; rotulo: string; detalhe: string | null }
interface Resumo {
  alvo: Alvo;
  simulacao: boolean;
  total: number;
  trilha: number;
  apagadas: Record<string, number>;
  desligadas: Record<string, number>;
  /** 285 (`RN-230`): os acessos ao portal que saem com o cliente. */
  portal?: AcessoQueSai[];
}

/** Um acesso ao portal que sai junto com o cliente (285). */
export interface AcessoQueSai { email: string; nome: string; fica_sem_cliente: boolean }

/**
 * O aviso do portal na exclusão — onda 285, `RN-230`.
 *
 * Apagar um cliente apaga o vínculo dos acessos ao portal com ele (0211:
 * `portal_pessoa_cliente.cliente_id` é obrigatório), e a trilha junto.
 * Quem fica sem nenhum outro cliente pede o código e não recebe — e a
 * tela de entrada, que responde igual para todos, não pode dizer por quê.
 * Foi o que calou os dois acessos do Humberto até 06/10. A janela diz
 * isso ANTES do clique, pelo e-mail de cada pessoa.
 *
 * Mora aqui porque as duas portas da exclusão — a lixeira das listas e a
 * tela `/exclusao-definitiva` — mostram o mesmo aviso.
 */
export function AvisoDoPortal({ portal, apagadas }: {
  portal?: AcessoQueSai[]; apagadas?: Record<string, number>;
}) {
  const lista = portal ?? [];
  const vinculos = Number(apagadas?.portal_pessoa_cliente ?? 0);
  if (lista.length === 0 && vinculos === 0) return null;
  const mudos = lista.filter((a) => a.fica_sem_cliente).length;
  const titulo = lista.length > 0
    ? (lista.length === 1
      ? 'Sai junto o acesso ao portal de 1 pessoa'
      : `Sai junto o acesso ao portal de ${lista.length} pessoas`)
    : `Sai junto ${num(vinculos)} vínculo(s) de acesso ao portal`;
  return (
    <div data-aviso-portal={mudos > 0 ? 'fica-sem-cliente' : 'sai'}>
      <Aviso tipo="atencao" titulo={titulo}>
        {lista.length > 0 && (
          <ul className="aviso-portal-lista">
            {lista.map((a) => (
              <li key={a.email}>
                <b>{a.email}</b>
                {' — '}
                {a.fica_sem_cliente
                  ? 'fica sem cliente e deixa de receber o código'
                  : 'continua entrando pelos outros clientes'}
              </li>
            ))}
          </ul>
        )}
        {(mudos > 0 || lista.length === 0) && (
          <p>
            Quem fica sem cliente pede o código e não recebe nada — a tela
            de entrada responde igual para todos e não pode dizer por quê.
            Para manter o acesso, vincule a pessoa a outro cliente em Saída →
            Portal do cliente → Quem tem acesso.
          </p>
        )}
      </Aviso>
    </div>
  );
}

/** O nome que a casa usa, para as tabelas que mais aparecem. */
const NOME_DA_TABELA: Record<string, string> = {
  ordem_servico: 'Ordem de serviço',
  os_status_historico: 'Histórico de status da OS',
  os_item_escopo: 'Itens do escopo da OS',
  os_amostra_prevista: 'Amostras previstas',
  os_ensaio_previsto: 'Ensaios previstos',
  os_revisao: 'Revisões da OS',
  lote_recebimento: 'Relatórios de recebimento',
  recebimento_evidencia: 'Fotos do recebimento',
  amostra: 'Amostras',
  amostra_movimentacao: 'Movimentações da amostra (custódia)',
  ensaio: 'Ensaios',
  ensaio_dado_bruto: 'Dados brutos de ensaio',
  ensaio_status_historico: 'Histórico de status do ensaio',
  ensaio_equipamento: 'Uso de equipamento em ensaio',
  ensaio_evidencia: 'Fotos de ensaio',
  relatorio: 'Laudos',
  equipamento: 'Equipamentos (e sensores)',
  categoria_equipamento: 'Categorias (grupos) do parque',
  calibracao_externa: 'Calibrações',
  verificacao_interna: 'Verificações internas',
  equipamento_manutencao: 'Manutenções',
  obra: 'Obras',
  cliente: 'Clientes',
  portal_pessoa_cliente: 'Acessos ao portal (vínculo com o cliente)',
};
export const nomeDaTabela = (t: string) => NOME_DA_TABELA[t] ?? t;
const num = (n: number) => Number(n).toLocaleString('pt-BR');

/** Quem vê a lixeira: a mesma regra do item da engrenagem. */
export function useLixeira(): boolean {
  const { eu } = useSessao();
  return !!eu?.exclusaoDefinitiva;
}

interface PropsDaLixeira {
  /** A chave do tipo na exclusão definitiva: `os`, `amostra`, `recebimento`… */
  tipo: string;
  /** O `id` que a rota entende (uuid; o código, para a categoria). */
  id: string;
  /** Como a linha se chama na tela: «OS 2606101», «2606101-001». */
  rotulo: string;
  /** Depois de apagar: a lista se recarrega (o que saiu junto sai dela também). */
  aoApagar(): void;
}

/**
 * O botão da lixeira e a janela de confirmação. Fica dentro de uma linha
 * de tabela: os cliques e as teclas da janela não sobem para a linha (que
 * abriria a ficha) — o React leva o evento pela árvore dos componentes,
 * não pela da página, e a janela mora dentro da célula.
 */
export function Lixeira({ tipo, id, rotulo, aoApagar }: PropsDaLixeira) {
  const pode = useLixeira();
  const [aberta, setAberta] = useState(false);
  if (!pode || !id) return null;
  return (
    <span className="lixeira-da-linha"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}>
      <button type="button" className="lixeira so-icone"
        aria-label={`Excluir ${rotulo} de vez`}
        title="Excluir de vez (homologação)"
        onClick={() => setAberta(true)} />
      {aberta && (
        <JanelaDaLixeira tipo={tipo} id={id} rotulo={rotulo}
          aoFechar={() => setAberta(false)}
          aoApagar={() => { setAberta(false); aoApagar(); }} />
      )}
    </span>
  );
}

function JanelaDaLixeira({ tipo, id, rotulo, aoFechar, aoApagar }: {
  tipo: string; id: string; rotulo: string; aoFechar(): void; aoApagar(): void;
}) {
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [apagando, setApagando] = useState(false);

  useEffect(() => {
    let vivo = true;
    api<Resumo>('/exclusao-definitiva/simular', { corpo: { tipo, id } })
      .then((r) => { if (vivo) setResumo(r); })
      .catch((e) => { if (vivo) setErro(mensagemDoErro(e)); });
    return () => { vivo = false; };
  }, [tipo, id]);

  const apagadas = useMemo(
    () => Object.entries(resumo?.apagadas ?? {}).sort((a, b) => b[1] - a[1]),
    [resumo]);
  const desligadas = useMemo(
    () => Object.values(resumo?.desligadas ?? {}).reduce((s, n) => s + Number(n), 0),
    [resumo]);

  const apagar = async () => {
    if (!resumo) return;
    setApagando(true);
    setErro(null);
    try {
      // a confirmação é o código que a própria simulação devolveu: a rota
      // continua conferindo que se apaga o que se viu.
      await api<Resumo>('/exclusao-definitiva/executar',
        { corpo: { tipo, id, confirmacao: resumo.alvo.rotulo } });
      aoApagar();
    } catch (e) {
      setErro(mensagemDoErro(e));
      setApagando(false);
    }
  };

  let corpo: ReactNode;
  if (!resumo && !erro) {
    corpo = <Carregando texto="Vendo o que sai junto (nada é apagado ainda)" />;
  } else if (resumo) {
    corpo = (
      <>
        <p className="lixeira-alvo">
          <b>{resumo.alvo.rotulo}</b>
          {resumo.alvo.detalhe && <span> · {resumo.alvo.detalhe}</span>}
        </p>
        <p>
          Sai <b>{num(resumo.total)}</b> linha(s) em <b>{apagadas.length}</b> tabela(s)
          {' '}e <b>{num(resumo.trilha)}</b> linha(s) da trilha de auditoria.
          {' '}Não tem desfazer.
        </p>
        <ul className="lixeira-tabelas" aria-label="O que sai junto">
          {apagadas.map(([t, n]) => (
            <li key={t}><span>{nomeDaTabela(t)}</span><span className="num">{num(n)}</span></li>
          ))}
        </ul>
        {desligadas > 0 && (
          <p className="sub">
            {num(desligadas)} linha(s) de outros registros ficam, mas deixam de
            apontar para o que foi apagado.
          </p>
        )}
        <AvisoDoPortal portal={resumo.portal} apagadas={resumo.apagadas} />
      </>
    );
  }

  return (
    <Dialogo aberto titulo={`Excluir ${rotulo} de vez?`} aoFechar={aoFechar}>
      <div className="janela-lixeira">
        {corpo}
        {erro && <Aviso tipo="erro" titulo={erro} />}
        <div className="linha">
          <button type="button" className="botao secundario" onClick={aoFechar} disabled={apagando}>
            Cancelar
          </button>
          {resumo && (
            <button type="button" className="botao perigo"
              onClick={() => { void apagar(); }} disabled={apagando}>
              {apagando ? 'Apagando…' : 'Excluir de vez'}
            </button>
          )}
        </div>
      </div>
    </Dialogo>
  );
}
